<template>
  <div class="dashboard-container">
    <!-- Sidebar -->
    <aside class="sidebar" :class="{ 'sidebar-collapsed': isCollapsed }">
      <div class="sidebar-header">
        <button class="toggle-btn" @click="toggleSidebar">
          <span v-if="!isCollapsed">☰</span>
          <span v-else>→</span>
        </button>
        <h2 v-if="!isCollapsed" class="sidebar-title">Property Management</h2>
      </div>
      
      <nav class="sidebar-nav">
        <router-link to="/" class="nav-item" active-class="active">
          <span class="nav-icon">🏠</span>
          <span v-if="!isCollapsed" class="nav-text">Dashboard</span>
        </router-link>
        <router-link to="/reservations" class="nav-item" active-class="active">
          <span class="nav-icon">📋</span>
          <span v-if="!isCollapsed" class="nav-text">Reservations</span>
        </router-link>
        <router-link to="/properties" class="nav-item" active-class="active">
          <span class="nav-icon">🏨</span>
          <span v-if="!isCollapsed" class="nav-text">Properties</span>
        </router-link>
        <button @click="openEmailContactsModal" class="nav-item nav-button">
          <span class="nav-icon">📧</span>
          <span v-if="!isCollapsed" class="nav-text">Email Contacts</span>
        </button>
        <router-link to="/settings" class="nav-item" active-class="active">
          <span class="nav-icon">⚙️</span>
          <span v-if="!isCollapsed" class="nav-text">Settings</span>
        </router-link>
      </nav>
    </aside>

    <!-- Main Content -->
    <main class="main-content">
      <div class="content-header">
        <h1>Dashboard</h1>
        <div class="user-info">
          <span>Welcome back!</span>
          <button @click="logout" class="logout-btn">Logout</button>
        </div>
      </div>

      <!-- SECTION 1: Global Statistics -->
      <div class="global-stats-section">
        <h2 class="section-title">Global Statistics (All Properties)</h2>
        <div v-if="loadingGlobalStats" class="section-spinner">
          <div class="spinner"></div>
        </div>
        <div v-else class="kpi-grid">
          <div class="kpi-card">
            <div class="kpi-icon">🏨</div>
            <div class="kpi-content">
              <h3>Total Properties</h3>
              <div class="kpi-value">{{ globalStats.totalProperties || 0 }}</div>
            </div>
          </div>

          <div class="kpi-card">
            <div class="kpi-icon">📅</div>
            <div class="kpi-content">
              <h3>Arrivals Today</h3>
              <div class="kpi-value">{{ globalStats.totalArrivingToday || 0 }}</div>
            </div>
          </div>

          <div class="kpi-card">
            <div class="kpi-icon">✅</div>
            <div class="kpi-content">
              <h3>Active Reservations</h3>
              <div class="kpi-value">{{ globalStats.totalActiveReservations || 0 }}</div>
            </div>
          </div>

          <div class="kpi-card">
            <div class="kpi-icon">📚</div>
            <div class="kpi-content">
              <h3>Total Bookings</h3>
              <div class="kpi-value">{{ globalStats.totalBookings || 0 }}</div>
              <div class="kpi-subtitle">This month</div>
            </div>
          </div>

          <div class="kpi-card">
            <div class="kpi-icon">💼</div>
            <div class="kpi-content">
              <h3>Management Commission</h3>
              <div class="kpi-value">{{ formatCurrency(globalStats.totalManagementCommission || 0) }}</div>
              <div class="kpi-subtitle">This month</div>
            </div>
          </div>

          <div class="kpi-card global-revenue">
            <div class="kpi-icon">💰</div>
            <div class="kpi-content">
              <h3>Owner Net Earnings</h3>
              <div v-if="!ownerNetEarningsCalculated && !calculatingOwnerNetEarnings" class="see-btn-wrapper">
                <button @click="calculateOwnerNetEarnings" class="see-btn">
                  See Actual Profit
                </button>
                <div class="kpi-subtitle">This month (after all expenses)</div>
              </div>
              <div v-else-if="calculatingOwnerNetEarnings" class="kpi-value calculating">
                <div class="mini-spinner"></div>
                <div class="kpi-subtitle">Calculating...</div>
              </div>
              <div v-else>
                <div class="kpi-value">{{ formatCurrency(globalStats.totalOwnerNetEarnings || 0) }}</div>
                <div class="kpi-subtitle">This month (after all expenses)</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SECTION 2: Reservations Arriving Today (All Properties) -->
      <div class="global-reservations-section">
        <h2 class="section-title">Reservations Arriving Today</h2>
        <div class="section-card">
          <div class="section-header">
            <span class="count-badge">{{ globalArrivingToday.length || 0 }}</span>
          </div>

          <div class="table-container" v-if="globalArrivingToday && globalArrivingToday.length > 0">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Guest Name</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  <th>Nights</th>
                  <th>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="reservation in globalArrivingToday.slice(0, 10)" :key="reservation.id">
                  <td><strong>{{ reservation.propertyName }}</strong></td>
                  <td>{{ reservation.guest_name || reservation.guestName }}</td>
                  <td>{{ formatDate(reservation.arrival_date || reservation.arrivalDate) }}</td>
                  <td>{{ formatDate(reservation.departure_date || reservation.departureDate) }}</td>
                  <td class="text-center">{{ reservation.total_nights || reservation.totalNights }}</td>
                  <td class="amount">{{ formatCurrency(reservation.total_reservation_price || reservation.totalReservationPrice) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="empty-state">
            <p>No reservations arriving today</p>
          </div>
        </div>
      </div>

      <!-- SECTION 3: Active Reservations (All Properties) -->
      <div class="global-reservations-section">
        <h2 class="section-title">Active Reservations</h2>
        <div class="section-card">
          <div class="section-header">
            <span class="count-badge">{{ globalActiveReservations.length || 0 }}</span>
          </div>

          <div class="table-container" v-if="globalActiveReservations && globalActiveReservations.length > 0">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Guest Name</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  <th>Nights</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="reservation in globalActiveReservations.slice(0, 15)" :key="reservation.id">
                  <td><strong>{{ reservation.propertyName }}</strong></td>
                  <td>{{ reservation.guest_name || reservation.guestName }}</td>
                  <td>{{ formatDate(reservation.arrival_date || reservation.arrivalDate) }}</td>
                  <td>{{ formatDate(reservation.departure_date || reservation.departureDate) }}</td>
                  <td class="text-center">{{ reservation.total_nights || reservation.totalNights }}</td>
                  <td class="amount">{{ formatCurrency(reservation.total_reservation_price || reservation.totalReservationPrice) }}</td>
                  <td>
                    <span class="status-badge active">Active</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="empty-state">
            <p>No active reservations</p>
          </div>
        </div>
      </div>

      <!-- SECTION 4: Property-Specific Reservations -->
      <div class="reservations-section">
        <h2 class="section-title">Reservations by Property</h2>
        
        <div v-if="loadingPropertyReservations" class="section-spinner">
          <div class="spinner"></div>
        </div>
        <div v-else>
          <!-- Property Selector -->
          <div class="property-dropdown">
            <label for="property-select">Show reservations for:</label>
            <select id="property-select" v-model="selectedPropertyForReservations" @change="fetchSelectedPropertyReservations">
              <option v-for="property in properties" :key="property.id" :value="property.id">
                {{ property.name }}
              </option>
            </select>
          </div>

        <!-- Reservations Booked Today -->
        <div class="section-card">
          <div class="section-header">
            <h2>Reservations Arriving Today</h2>
            <span class="count-badge">{{ selectedPropertyData.arrivingTodayCount || 0 }}</span>
          </div>
          
          <div class="table-container" v-if="selectedPropertyData.arrivingToday && selectedPropertyData.arrivingToday.length > 0">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Guest Name</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  <th>Nights</th>
                  <th>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="reservation in selectedPropertyData.arrivingToday.slice(0, 5)" :key="reservation.id">
                  <td>{{ reservation.guest_name || reservation.guestName }}</td>
                  <td>{{ formatDate(reservation.arrival_date || reservation.arrivalDate) }}</td>
                  <td>{{ formatDate(reservation.departure_date || reservation.departureDate) }}</td>
                  <td class="text-center">{{ reservation.total_nights || reservation.totalNights }}</td>
                  <td class="amount">{{ formatCurrency(reservation.total_reservation_price || reservation.totalReservationPrice) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div v-else class="empty-state">
            <p>No reservations arriving today for this property</p>
          </div>
        </div>

        <!-- Active Reservations -->
        <div class="section-card">
          <div class="section-header">
            <h2>Active Reservations</h2>
            <span class="count-badge">{{ selectedPropertyData.activeReservationsCount || 0 }}</span>
          </div>
          
          <div class="table-container" v-if="selectedPropertyData.activeReservations && selectedPropertyData.activeReservations.length > 0">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Guest Name</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  <th>Nights</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="reservation in selectedPropertyData.activeReservations.slice(0, 10)" :key="reservation.id">
                  <td>{{ reservation.guest_name || reservation.guestName }}</td>
                  <td>{{ formatDate(reservation.arrival_date || reservation.arrivalDate) }}</td>
                  <td>{{ formatDate(reservation.departure_date || reservation.departureDate) }}</td>
                  <td class="text-center">{{ reservation.total_nights || reservation.totalNights }}</td>
                  <td class="amount">{{ formatCurrency(reservation.total_reservation_price || reservation.totalReservationPrice) }}</td>
                  <td>
                    <span class="status-badge active">Active</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div v-else class="empty-state">
            <p>No active reservations for this property</p>
          </div>
          
          <div v-if="selectedPropertyData.activeReservations && selectedPropertyData.activeReservations.length > 10" class="view-all">
            <router-link to="/reservations" class="btn btn-outline">View All Reservations</router-link>
          </div>
        </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import SETTINGS from '@/config/settings.js';

export default {
  name: 'DashboardView',
  data() {
    return {
      isCollapsed: false,
      baseUrl: SETTINGS.API.baseUrl,
      
      // Properties data
      properties: [],
      
      // Global statistics (aggregated from all properties)
      globalStats: {
        totalProperties: 0,
        totalArrivingToday: 0,
        totalArrivingTodayCost: 0,
        totalActiveReservations: 0,
        totalActiveReservationsCost: 0,
        totalMonthRevenue: 0,
        totalBookings: 0,
        totalManagementCommission: 0,
        totalOwnerNetEarnings: 0
      },

      // Individual property data for calculations
      propertiesData: [],

      // Global reservations lists (all properties combined)
      globalArrivingToday: [],
      globalActiveReservations: [],

      // Selected property for reservations
      selectedPropertyForReservations: null,
      selectedPropertyData: {
        arrivingToday: [],
        arrivingTodayCount: 0,
        activeReservations: [],
        activeReservationsCount: 0
      },
      
      // Loading states
      loadingGlobalStats: true,
      loadingPropertyReservations: true,

      // Owner Net Earnings calculation
      ownerNetEarningsCalculated: false,
      calculatingOwnerNetEarnings: false
    };
  },
  
  methods: {
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
    },
    
    logout() {
      localStorage.removeItem('token');
      this.$router.push('/login');
    },
    
    openEmailContactsModal() {
      this.$router.push('/reservations?openContacts=true');
    },
    
    formatCurrency(amount) {
      return SETTINGS.formatCurrency(amount || 0);
    },
    
    formatDate(timestamp) {
      return SETTINGS.formatDate(timestamp);
    },

    async calculateOwnerNetEarnings() {
      this.calculatingOwnerNetEarnings = true;

      try {
        const token = localStorage.getItem('token');
        const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig'));
        const pmsConfigId = selectedPms.id;
        const headers = { 'Authorization': `Bearer ${token}` };

        let totalSettlementAmount = 0;

        console.log('=== CALCULATING OWNER NET EARNINGS ===');
        console.log('Total properties:', this.properties.length);

        // Pour chaque propriété, calculer le settlement amount
        for (const property of this.properties) {
          try {
            console.log(`\n--- Property: ${property.name} ---`);

            // 1. Récupérer les settings de la propriété
            const settingsRes = await axios.get(`${this.baseUrl}/api/pms/settings/`, {
              headers,
              params: {
                pms_config_id: pmsConfigId,
                property_id: property.id
              }
            });

            console.log('Settings response:', settingsRes.data);

            const settings = settingsRes.data.data || {
              managementFeeRate: 20,
              managementFeeBase: 'gross',
              vatRate: 20,
              maintenance: 0,
              consumables: 0,
              otherExpenses: 0,
              cleaningMode: 'rentlio',
              cardFeeMode: 'rentlio',
              platformFeeMode: 'rentlio'
            };

            // 2. Récupérer les réservations du mois
            const monthReservationsRes = await axios.get(`${this.baseUrl}/api/pms/reports/current/`, {
              headers,
              params: { pms_config_id: pmsConfigId, property_id: property.id }
            });

            const monthReservations = monthReservationsRes.data.reservations || [];
            const reservationsArray = Array.isArray(monthReservations) ? monthReservations : Object.values(monthReservations);

            console.log('Reservations count:', reservationsArray.length);
            console.log('First reservation sample:', reservationsArray[0]);

            // 3. Calculer comme dans le rapport
            let totalRevenue = 0;
            let totalPlatformFees = 0;
            let totalCardFees = 0;
            let totalOutgoings = 0;

            reservationsArray.forEach(res => {
              const amount = parseFloat(res.total_reservation_price || res.totalReservationPrice || 0);
              const commission = parseFloat(res.channel_commission_nominal || res.channelCommissionNominal || 0);
              const cardFee = parseFloat(res.card_fee || res.cardFee || 0);
              const outgoings = parseFloat(res.total_services_price || res.totalServicesPrice || 0);

              totalRevenue += amount;
              totalPlatformFees += commission;
              totalCardFees += cardFee;
              totalOutgoings += outgoings;
            });

            // 4. Net Income
            const netIncome = totalRevenue - totalPlatformFees - totalCardFees;

            // 5. Gross Profit
            const grossProfit = netIncome - totalOutgoings;

            // 6. Management Fees
            let managementFees = 0;
            if (settings.managementFeeBase === 'revenue' || settings.management_fee_base === 'revenue') {
              managementFees = totalRevenue * (parseFloat(settings.managementFeeRate || settings.management_fee_rate || 0) / 100);
            } else if (settings.managementFeeBase === 'gross' || settings.management_fee_base === 'gross') {
              managementFees = grossProfit * (parseFloat(settings.managementFeeRate || settings.management_fee_rate || 0) / 100);
            } else if (settings.managementFeeBase === 'grossFixed' || settings.management_fee_base === 'grossFixed') {
              managementFees = parseFloat(settings.managementFeeFixedAmount || settings.management_fee_fixed_amount || 0);
            } else {
              managementFees = netIncome * (parseFloat(settings.managementFeeRate || settings.management_fee_rate || 0) / 100);
            }

            // 7. Autres déductions
            const vat = managementFees * (parseFloat(settings.vatRate || settings.vat_rate || 0) / 100);
            const maintenance = parseFloat(settings.maintenance || 0);
            const consumables = parseFloat(settings.consumables || 0);
            const otherExpenses = parseFloat(settings.otherExpenses || settings.other_expenses || 0);

            // 8. Settlement Amount (Owner Net Earnings pour cette propriété)
            const settlementAmount = grossProfit - managementFees - vat - maintenance - consumables - otherExpenses;

            console.log('Calculations for this property:');
            console.log('  Total Revenue:', totalRevenue);
            console.log('  Net Income:', netIncome);
            console.log('  Gross Profit:', grossProfit);
            console.log('  Management Fees:', managementFees);
            console.log('  VAT:', vat);
            console.log('  Settlement Amount:', settlementAmount);

            totalSettlementAmount += settlementAmount;
          } catch (propertyError) {
            console.error(`Error calculating for property ${property.name}:`, propertyError);
            // Continue avec les autres propriétés
          }
        }

        console.log('\n=== FINAL TOTAL SETTLEMENT AMOUNT:', totalSettlementAmount, '===\n');

        // Mettre à jour la valeur globale
        this.globalStats.totalOwnerNetEarnings = totalSettlementAmount;
        this.ownerNetEarningsCalculated = true;

      } catch (error) {
        console.error('Error calculating Owner Net Earnings:', error);
        alert('Error calculating Owner Net Earnings. Please try again.');
      } finally {
        this.calculatingOwnerNetEarnings = false;
      }
    },

    async fetchDashboardData() {
      const token = localStorage.getItem('token');
      if (!token) {
        this.$router.push('/login');
        return;
      }

      // Get selected PMS configuration from localStorage
      const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig'));
      if (!selectedPms || !selectedPms.id) {
        alert('No PMS configuration selected. Please select a PMS from the Hub.');
        this.$router.push('/');
        return;
      }

      const pmsConfigId = selectedPms.id;
      const headers = { 'Authorization': `Bearer ${token}` };
      this.loadingGlobalStats = true;

      try {
        // Step 1: Fetch all properties using Django endpoint
        const propertiesRes = await axios.get(`${this.baseUrl}/api/pms/properties/`, {
          headers,
          params: { pms_config_id: pmsConfigId }
        });
        this.properties = propertiesRes.data.properties.data;
        
        // Set default selected property for reservations (first property)
        if (this.properties.length > 0 && !this.selectedPropertyForReservations) {
          this.selectedPropertyForReservations = this.properties[0].id;
        }
        
        // Properties are loaded
        
        // Step 2: Fetch data for each property separately with batching to avoid rate limiting
        this.propertiesData = [];
        const batchSize = 5; // Process 5 properties at a time
        const delayBetweenBatches = 1000; // 1 second delay between batches
        
        for (let i = 0; i < this.properties.length; i += batchSize) {
          const batch = this.properties.slice(i, i + batchSize);
          
          const batchPromises = batch.map(async (property) => {
            try {
              // Add small delay between individual requests within batch
              await new Promise(resolve => setTimeout(resolve, 200 * (i % batchSize)));
              
              const [arrivingTodayRes, activeTodayRes, monthRevenueRes] = await Promise.all([
                axios.get(`${this.baseUrl}/api/pms/reservations/`, {
                  headers,
                  params: { pms_config_id: pmsConfigId, filter: 'booked', property_id: property.id }
                }),
                axios.get(`${this.baseUrl}/api/pms/reservations/`, {
                  headers,
                  params: { pms_config_id: pmsConfigId, filter: 'today', property_id: property.id }
                }),
                axios.get(`${this.baseUrl}/api/pms/reports/current/`, {
                  headers,
                  params: { pms_config_id: pmsConfigId, property_id: property.id }
                })
              ]);
              
              // Parse Django API responses
              const arrivingToday = arrivingTodayRes.data.reservations?.data || [];
              const activeToday = activeTodayRes.data.reservations?.data || [];
              const monthReservations = monthRevenueRes.data.reservations || [];

              // Convert to array if it's an object
              const monthReservationsArray = Array.isArray(monthReservations)
                ? monthReservations
                : Object.values(monthReservations);

              // Calculate month statistics
              const monthRevenue = monthReservationsArray.reduce((total, res) =>
                total + (res.total_reservation_price || res.totalReservationPrice || 0), 0);

              const monthBookings = monthReservationsArray.length;

              const managementCommission = monthReservationsArray.reduce((total, res) =>
                total + (res.channel_commission_nominal || res.channelCommissionNominal || 0), 0);

              const ownerNetEarnings = monthReservationsArray.reduce((total, res) => {
                const revenue = res.total_reservation_price || res.totalReservationPrice || 0;
                const commission = res.channel_commission_nominal || res.channelCommissionNominal || 0;
                const cardFee = res.card_fee || res.cardFee || 0;
                return total + (revenue - commission - cardFee);
              }, 0);

              return {
                id: property.id,
                name: property.name,
                arrivingToday: arrivingToday,
                arrivingTodayCount: arrivingToday.length,
                arrivingTodayCost: arrivingToday.reduce((total, res) => total + (res.total_reservation_price || res.totalReservationPrice || 0), 0),
                activeReservations: activeToday,
                activeReservationsCount: activeToday.length,
                activeReservationsCost: activeToday.reduce((total, res) => total + (res.total_reservation_price || res.totalReservationPrice || 0), 0),
                monthRevenue: monthRevenue,
                monthBookings: monthBookings,
                managementCommission: managementCommission,
                ownerNetEarnings: ownerNetEarnings
              };
            } catch (error) {
              console.error(`Error fetching data for property ${property.name}:`, error);
              return {
                id: property.id,
                name: property.name,
                arrivingToday: [],
                arrivingTodayCount: 0,
                arrivingTodayCost: 0,
                activeReservations: [],
                activeReservationsCount: 0,
                activeReservationsCost: 0,
                monthRevenue: 0,
                monthBookings: 0,
                managementCommission: 0,
                ownerNetEarnings: 0
              };
            }
          });
          
          // Process current batch
          const batchResults = await Promise.all(batchPromises);
          this.propertiesData.push(...batchResults);
          
          // Update global stats after each batch for progressive loading
          this.calculateGlobalStats();
          
          // After first batch, property reservations section can be shown
          if (i === 0) {
            this.loadingPropertyReservations = false;
            // Load selected property reservations
            this.fetchSelectedPropertyReservations();
          }
          
          // Add delay before next batch (except for last batch)
          if (i + batchSize < this.properties.length) {
            await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
          }
        }


        // Final step: Show global stats
        
        // All data loaded, show global statistics
        this.loadingGlobalStats = false;
        
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
        }
      } finally {
        // Ensure all loading states are disabled even if there's an error
        this.loadingGlobalStats = false;
        this.loadingPropertyReservations = false;
      }
    },
    
    calculateGlobalStats() {

      const totalArrivingToday = this.propertiesData?.reduce((total, prop) => total + (prop.arrivingTodayCount || 0), 0) || 0;
      const totalArrivingTodayCost = this.propertiesData?.reduce((total, prop) => total + (prop.arrivingTodayCost || 0), 0) || 0;
      const totalActiveReservations = this.propertiesData?.reduce((total, prop) => total + (prop.activeReservationsCount || 0), 0) || 0;
      const totalActiveReservationsCost = this.propertiesData?.reduce((total, prop) => total + (prop.activeReservationsCost || 0), 0) || 0;
      const totalMonthRevenue = this.propertiesData?.reduce((total, prop) => total + (prop.monthRevenue || 0), 0) || 0;
      const totalBookings = this.propertiesData?.reduce((total, prop) => total + (prop.monthBookings || 0), 0) || 0;
      const totalManagementCommission = this.propertiesData?.reduce((total, prop) => total + (prop.managementCommission || 0), 0) || 0;
      const totalOwnerNetEarnings = this.propertiesData?.reduce((total, prop) => total + (prop.ownerNetEarnings || 0), 0) || 0;

      // Build global reservations lists (all properties combined)
      this.globalArrivingToday = [];
      this.globalActiveReservations = [];

      this.propertiesData?.forEach(prop => {
        // Add property name to each reservation for display
        if (prop.arrivingToday && Array.isArray(prop.arrivingToday)) {
          prop.arrivingToday.forEach(res => {
            this.globalArrivingToday.push({
              ...res,
              propertyName: prop.name
            });
          });
        }

        if (prop.activeReservations && Array.isArray(prop.activeReservations)) {
          prop.activeReservations.forEach(res => {
            this.globalActiveReservations.push({
              ...res,
              propertyName: prop.name
            });
          });
        }
      });

      // Ensure globalStats always has proper structure with default values
      this.globalStats = {
        totalProperties: this.properties?.length || 0,
        totalArrivingToday: totalArrivingToday,
        totalArrivingTodayCost: totalArrivingTodayCost,
        totalActiveReservations: totalActiveReservations,
        totalActiveReservationsCost: totalActiveReservationsCost,
        totalMonthRevenue: totalMonthRevenue,
        totalBookings: totalBookings,
        totalManagementCommission: totalManagementCommission,
        totalOwnerNetEarnings: totalOwnerNetEarnings
      };

    },
    
    async fetchSelectedPropertyReservations() {
      if (!this.selectedPropertyForReservations) return;
      
      const propertyData = this.propertiesData.find(p => p.id == this.selectedPropertyForReservations);
      if (propertyData) {
        this.selectedPropertyData = {
          arrivingToday: propertyData.arrivingToday,
          arrivingTodayCount: propertyData.arrivingTodayCount,
          activeReservations: propertyData.activeReservations,
          activeReservationsCount: propertyData.activeReservationsCount
        };
      }
    },
    
    initializeDefaults() {
      // Initialize all data structures to prevent undefined errors
      this.globalStats = {
        totalProperties: 0,
        totalArrivingToday: 0,
        totalArrivingTodayCost: 0,
        totalActiveReservations: 0,
        totalActiveReservationsCost: 0,
        totalMonthRevenue: 0
      };
      
      this.selectedPropertyData = {
        arrivingToday: [],
        arrivingTodayCount: 0,
        activeReservations: [],
        activeReservationsCount: 0
      };

      // Initialize loading states
      this.loadingGlobalStats = true;
      this.loadingPropertyReservations = true;
    }
  },
  
  created() {
    // Ensure globalStats is properly initialized even before API calls
    this.initializeDefaults();
  },
  
  mounted() {
    this.fetchDashboardData();
  }
};
</script>

<style scoped>
.dashboard-container {
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
}

/* Sidebar Styles */
.sidebar {
  width: 280px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  transition: width 0.3s ease;
  position: fixed;
  height: 100vh;
  z-index: 1000;
  box-shadow: 2px 0 10px rgba(0,0,0,0.1);
}

.sidebar-collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 20px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.toggle-btn {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-bottom: 15px;
}

.sidebar-title {
  color: white;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.sidebar-nav {
  padding: 20px 0;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  color: rgba(255,255,255,0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  margin: 0 10px;
  border-radius: 10px;
}

.nav-item:hover,
.nav-item.active {
  background: rgba(255,255,255,0.1);
  color: white;
  transform: translateX(5px);
}

.nav-button {
  background: none;
  border: none;
  width: 100%;
  cursor: pointer;
  text-align: left;
}

.nav-icon {
  font-size: 18px;
  margin-right: 15px;
  width: 20px;
}

.nav-text {
  font-weight: 500;
}

/* Main Content */
.main-content {
  flex: 1;
  margin-left: 280px;
  padding: 30px;
  transition: margin-left 0.3s ease;
}

.sidebar-collapsed + .main-content {
  margin-left: 70px;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.content-header h1 {
  font-size: 32px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.logout-btn {
  background: #e53e3e;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.3s ease;
}

.logout-btn:hover {
  background: #c53030;
}

/* KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 25px;
  margin-bottom: 40px;
}

.kpi-card {
  background: white;
  padding: 25px;
  border-radius: 16px;
  box-shadow: 0 4px 25px rgba(0,0,0,0.08);
  display: flex;
  align-items: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid #e2e8f0;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 35px rgba(0,0,0,0.12);
}

.kpi-icon {
  font-size: 40px;
  margin-right: 20px;
  opacity: 0.8;
}

.kpi-content h3 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.kpi-value {
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 4px;
}

.kpi-subtitle {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.see-btn-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.see-btn {
  background: #e2e8f0;
  color: #1a202c;
  border: 2px solid #cbd5e0;
  padding: 12px 32px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.see-btn:hover {
  background: #cbd5e0;
  border-color: #a0aec0;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.see-btn:active {
  transform: translateY(0);
}

.kpi-value.calculating {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.mini-spinner {
  border: 3px solid #f3f4f6;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Section Cards */
.section-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 25px rgba(0,0,0,0.08);
  margin-bottom: 30px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.section-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
}

.count-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
}

/* Table Styles */
.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: #f8fafc;
  padding: 16px 20px;
  text-align: left;
  font-weight: 600;
  color: #4a5568;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e2e8f0;
}

.data-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  color: #2d3748;
  vertical-align: middle;
}

.data-table tbody tr:hover {
  background: #f7fafc;
}

.property-name {
  font-weight: 600;
  color: #2b6cb0;
}

.amount {
  text-align: right;
  font-weight: 600;
  color: #22543d;
}

.text-center {
  text-align: center;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.active {
  background: #c6f6d5;
  color: #22543d;
}

/* Empty State */
.empty-state {
  padding: 60px 20px;
  text-align: center;
  color: #64748b;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
}

/* View All */
.view-all {
  padding: 20px 30px;
  text-align: center;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.btn {
  display: inline-flex;
  align-items: center;
  padding: 10px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.btn-outline {
  background: transparent;
  color: #667eea;
  border-color: #667eea;
}

.btn-outline:hover {
  background: #667eea;
  color: white;
  transform: translateY(-1px);
}

/* Section Styles */
.global-stats-section,
.global-reservations-section,
.reservations-section {
  margin-bottom: 40px;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 20px 0;
  text-align: center;
}

/* Global Revenue Card Special Style */
.global-revenue {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.global-revenue .kpi-content h3,
.global-revenue .kpi-value {
  color: white;
}

/* Property Selector Styles */
/* Property Dropdown Styles */
.property-dropdown {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 25px rgba(0,0,0,0.08);
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 15px;
}

.property-dropdown label {
  font-weight: 600;
  color: #1a202c;
  white-space: nowrap;
}

.property-dropdown select {
  flex: 1;
  padding: 10px 15px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  font-size: 14px;
  font-weight: 500;
  color: #1a202c;
  cursor: pointer;
  transition: border-color 0.3s ease;
}

.property-dropdown select:focus {
  outline: none;
  border-color: #667eea;
}

/* Responsive Design */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }
  
  .main-content {
    margin-left: 0;
    padding: 20px 15px;
  }
  
  .kpi-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .global-revenue {
    grid-column: 1;
  }
  
  .content-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .section-header {
    padding: 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .data-table {
    font-size: 12px;
  }
  
  .data-table th,
  .data-table td {
    padding: 12px 8px;
  }

  .property-dropdown {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  
  .property-dropdown label {
    white-space: normal;
  }
}

/* Section Spinner */
.section-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  min-height: 200px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>